import { types, getParent } from 'mobx-state-tree';
import State from '../metamodel/state';
import { modelReference } from '../refHelper';
import type { IState } from '../metamodel/state';
import type { IFSMLink } from './fsmlink';
import type { IFSMView } from './fsm';

const DEFAULT_WIDTH = 100;
const DEFAULT_HEIGHT = 50;
const DEFAULT_X = 0;
const DEFAULT_Y = 0;

/**
 * The FSMNode instance interface.
 *
 * HAND-DECLARED because FSMNode sits on the FSM recursion boundary: it is held in
 * `IFSMView.nodes` AND reaches `IFSMView` back through `get selfLinks()` (which
 * calls `getParent(self, 2)`). Typing those against the interfaces cuts the cycle
 * instead of re-inferring `typeof FSMNode`/`typeof FSMView`.
 */
export interface IFSMNode {
  readonly state: IState;
  readonly position: { x: number; y: number };
  readonly size: { height: number; width: number };
  readonly selfLinks: (IFSMLink | undefined)[];
  updatePosition(x: number, y: number): void;
  updateSize(width: number, height: number): void;
}

/**
 * A node in an FSM. This is the visual counterpart of a state. It has a
 * position (x, y) and a size (width, height).
 *
 * For the references to the state, we don't use types.reference directly,
 * but our custom wrapper. See '../refHelper.js' for the reason why.
 */
const FSMNode = types
  .model('FSMNode', {
    state: modelReference<typeof State>(State),
    position: types.optional(
      types.model({
        x: types.number,
        y: types.number,
      }),
      () => ({ x: DEFAULT_X, y: DEFAULT_Y })
    ),
    size: types.optional(
      types.model({
        height: types.number,
        width: types.number,
      }),
      () => ({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT })
    ),
  })
  .views((self0) => {
    const self = self0 as unknown as IFSMNode;
    return {
      get selfLinks(): (IFSMLink | undefined)[] {
        const uifsm: IFSMView = getParent(self0, 2);
        const selfTransitions = self.state.selfTransitions.map((t) => t.id);
        return selfTransitions.map((id) => uifsm.findLinkById(id));
      },
    };
  })
  .actions((self0) => {
    const self = self0 as unknown as IFSMNode;
    return {
      updatePosition(x: number, y: number) {
        const moveX = x - self.position.x;
        const moveY = y - self.position.y;
        self0.position.x = x;
        self0.position.y = y;
        for (const sl of self.selfLinks) {
          sl!.moveVertices(moveX, moveY);
        }
      },
      updateSize(width: number, height: number) {
        self0.size.width = width;
        self0.size.height = height;
      },
    };
  });

export default FSMNode;
